import { useEffect, useRef } from "react"

import "./gameplay-tour-view.css"

interface GameplayTourViewProps {
  canAdvance: boolean
  // Move count shown in the start-tour confirmation copy (sortaballs only).
  moveCount?: number
  onCancelStart?: ()=> void
  onClose: ()=> void
  onConfirmStart?: ()=> void
  onContinue: ()=> void
  // Sortaballs: a queued tour start on a board with real progress (3+ moves)
  // awaits the player's OK before the tour resets the board.
  pendingStartConfirm?: boolean
  stepText: null | string
}

// Floating guide card pinned to the bottom edge of the play area, shared by
// every game's guided tour. Rendered by the game that owns the tour; narration
// text + Continue/Close controls. A sortaballs tour that would reset a
// progressed board first mounts a confirmation dialog, mirroring the header's
// reset confirmation (same threshold and styling).
export function GameplayTourView({
  canAdvance,
  moveCount,
  onCancelStart,
  onClose,
  onConfirmStart,
  onContinue,
  pendingStartConfirm = false,
  stepText,
}: GameplayTourViewProps) {
  const confirmRef = useRef<HTMLDialogElement>(null)

  // Native <dialog>, toggled imperatively via ref (no React state) — same
  // pattern as the reset confirmation in PuzzleHeader.
  useEffect(()=> {
    const dialog = confirmRef.current
    if (!dialog || !pendingStartConfirm || dialog.open) {
      return
    }
    dialog.showModal()
  }, [pendingStartConfirm])

  if (stepText === null && !pendingStartConfirm) {
    return null
  }

  return (
    <>
      {stepText !== null && (
        <div
          aria-label="Game guide"
          className="gameplay-tour"
          onClick={(event)=> event.stopPropagation()}
          role="dialog"
        >
          <div className="gameplay-tour__text" data-tour-text>
            {stepText}
          </div>
          <div className="gameplay-tour__actions">
            <button disabled={!canAdvance} onClick={onContinue} type="button">
              Continue
            </button>
            <button onClick={onClose} type="button">
              Close
            </button>
          </div>
        </div>
      )}
      {pendingStartConfirm && (
        <dialog
          aria-label="Start the tour and reset the board?"
          aria-modal="true"
          className="sortaballs-reset-confirm"
          onClick={(event)=> {
            // Clicking the backdrop (the dialog element itself, outside the
            // box) cancels without resetting the board.
            if (event.target === event.currentTarget) {
              onCancelStart?.()
            }
          }}
          ref={confirmRef}
        >
          <div className="sortaballs-reset-confirm__box">
            <p className="sortaballs-reset-confirm__text">
              You've made {moveCount ?? 0} moves. Starting the tour resets the
              board — start over?
            </p>
            <div className="sortaballs-reset-confirm__actions">
              <button
                className="sortaballs-reset-confirm__btn"
                onClick={onCancelStart}
                type="button"
              >
                Cancel
              </button>
              <button
                className="sortaballs-reset-confirm__btn sortaballs-reset-confirm__btn--danger"
                onClick={onConfirmStart}
                type="button"
              >
                Start tour
              </button>
            </div>
          </div>
        </dialog>
      )}
    </>
  )
}